import type { ShotInput, SportsGameSettings } from '../../sports.types';
import type {
  ISportDefinition,
  SportAssets,
  SportPhysicsParams,
  StartingPosition,
  ShotEvaluation,
} from '../sport-definition.interface';

// ─── Yardage distance system ───────────────────────────────────────────────────

/**
 * All 7 yardage options, ordered by index 0–6 (index 3 = 50 yards = center).
 * Difficulty increases with index (farther = harder mechanics).
 */
const ALL_YARDS = [20, 30, 40, 50, 60, 70, 80] as const;

/**
 * Yardage → scene units mapping.
 * Shorter kicks = closer goal = easier (target looks larger, angle forgiving).
 * Longer kicks = farther goal = harder (smaller visual, tighter power/angle window).
 */
const YARDS_TO_SCENE: Record<number, number> = {
  20: 1.5,
  30: 2.0,
  40: 2.5,
  50: 3.0,
  60: 3.5,
  70: 4.0,
  80: 4.5,
};

const CENTER_INDEX = 3; // index of 50 yards in ALL_YARDS

// ─── Physics constants ─────────────────────────────────────────────────────────

const PHYSICS: SportPhysicsParams = {
  ballRadius: 0.11,         // American football prolate spheroid ≈ 11 cm short axis
  launchSpeedScale: 6.0,
  arcHeightFactor: 1.0,     // High kick arc — ball rises well above the crossbar
  assistCorrectionStrength: 0.6,
  goalToleranceRadius: 0.35, // Half the upright gap in scene units
  optimalElevationDeg: 45,
  shankBaseThreshold: 0.85,
  shankDriftScale: 2.0,
};

// ─── Assets ────────────────────────────────────────────────────────────────────

const ASSETS: SportAssets = {
  // Phase 3+: ballModelPath: 'assets/sports/football/football.glb'
  ballColor: '#8B4513', // brown
};

// ─── Sport definition ──────────────────────────────────────────────────────────

export class FootballSportDefinition implements ISportDefinition {
  readonly id = 'football' as const;
  readonly displayName = 'Field Goal';
  readonly scoringModel = 'binary' as const;
  readonly assets = ASSETS;
  readonly physicsParams = PHYSICS;
  readonly maxAttempts = 7;

  /**
   * Fallback static positions (all 7 yardages, ascending).
   * Used only if getStartingPositions() is not called.
   */
  readonly startingPositions: StartingPosition[] = ALL_YARDS.map((yards) => ({
    lateralOffset: 0,
    distance: YARDS_TO_SCENE[yards],
    pointsIfScored: 3,
  }));

  readonly arCameraHint = { pitch: -10, yaw: 0 };
  readonly arSceneRotation: [number, number, number] = [-Math.PI / 2, 0, 0];

  /**
   * Returns a symmetric subset of the 7 yardage distances based on attempt count.
   *
   * Odd counts: always include 50 yards (the center) + symmetric flanks.
   * Even counts: symmetric pairs around the 40/60 gap (no 50-yard attempt).
   * Result sorted ascending (easiest first → hardest last).
   *
   * | n | Distances (yards)              |
   * |---|--------------------------------|
   * | 1 | 50                             |
   * | 2 | 40, 60                         |
   * | 3 | 40, 50, 60                     |
   * | 4 | 30, 40, 60, 70                 |
   * | 5 | 30, 40, 50, 60, 70             |
   * | 6 | 20, 30, 40, 60, 70, 80         |
   * | 7 | 20, 30, 40, 50, 60, 70, 80     |
   */
  getStartingPositions(attempts: number): StartingPosition[] {
    const n = Math.min(Math.max(1, attempts), 7);
    const selected: number[] = [];

    if (n % 2 === 1) {
      // Odd: start with center (50 yards), expand symmetrically outward
      selected.push(CENTER_INDEX);
      for (let i = 1; selected.length < n; i++) {
        selected.push(CENTER_INDEX - i);
        if (selected.length < n) selected.push(CENTER_INDEX + i);
      }
    } else {
      // Even: symmetric pairs around the 40/60 gap
      for (let i = 1; selected.length < n; i++) {
        selected.push(CENTER_INDEX - i);
        if (selected.length < n) selected.push(CENTER_INDEX + i);
      }
    }

    return selected
      .sort((a, b) => a - b)
      .map((idx) => ({
        lateralOffset: 0,
        distance: YARDS_TO_SCENE[ALL_YARDS[idx]],
        pointsIfScored: 3,
      }));
  }

  evaluateShot(
    input: ShotInput,
    assist: number,
    settings: SportsGameSettings
  ): ShotEvaluation {
    const {
      launchSpeedScale,
      optimalElevationDeg = 45,
      goalToleranceRadius,
      assistCorrectionStrength,
      shankBaseThreshold = 0.85,
      shankDriftScale = 2.0,
    } = this.physicsParams;

    // ── Step 1: Whiff check ──────────────────────────────────────────────────
    if (input.overSwing) {
      return {
        scored: false,
        pointsEarned: 0,
        finalPosition: { x: 0, y: 0, z: 0 },
        isWhiff: true,
      };
    }

    // ── Step 2: Trajectory ───────────────────────────────────────────────────
    const launchAngleDeg = input.direction.y * optimalElevationDeg * 2;
    const launchAngleRad = (launchAngleDeg * Math.PI) / 180;
    const cosAngle = Math.cos(launchAngleRad);

    const baseDistance = input.power * launchSpeedScale * cosAngle;

    // ── Step 3: Assist correction on lateral aim ─────────────────────────────
    const rawLateral = input.direction.x;
    const correctedLateral = rawLateral * (1 - assist * assistCorrectionStrength);

    // ── Step 4: Shank (lateral drift on over-powered kick) ───────────────────
    const shankThreshold = shankBaseThreshold + assist * 0.1;
    const shankDrift =
      input.power > shankThreshold
        ? (input.power - shankThreshold) * shankDriftScale * (Math.random() > 0.5 ? 1 : -1)
        : 0;

    const finalX = correctedLateral * baseDistance * 0.3 + shankDrift;

    // ── Step 5: Determine if ball passed through the uprights ────────────────
    // The uprights are at the goal (distance from physicsParams).
    // We check: did the ball arrive within goalToleranceRadius of center?
    const scored = Math.abs(finalX) <= goalToleranceRadius;
    const pointsEarned = scored ? (settings.pointsPerSuccess || 3) : 0;

    return {
      scored,
      pointsEarned,
      // y: 2.6 = mid-upright height in sports-scene.component.ts (crossbar at 2.0, tips at 3.2)
      finalPosition: { x: finalX, y: 2.6, z: -baseDistance },
      isWhiff: false,
    };
  }
}
